import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';

import { Toast } from './Toast';
import { ToastContext, type ToastApi, type ToastOptions, type ToastRecord } from './toastContext';
import styles from './Toast.module.css';

const DEFAULT_DURATION_MS = 6_000;
/** Toasts in the collapsed stack; any older ones wait, hidden, behind the third. */
const VISIBLE = 3;
/** How much of each toast behind peeks out of the collapsed stack. */
const PEEK_PX = 10;
const GAP_PX = 8;
/** Time for a dismissed toast's exit before it leaves the list. */
const EXIT_MS = 200;

let sequence = 0;

export interface ToastProviderProps {
  children: ReactNode;
  /** Cap on simultaneously visible toasts; the oldest is dropped past this. */
  limit?: number | undefined;
}

interface Timer {
  handle: ReturnType<typeof setTimeout> | undefined;
  remaining: number;
  startedAt: number;
}

function prefersReducedMotion(): boolean {
  return typeof window.matchMedia === 'function'
    ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
    : false;
}

/**
 * Toasts stack instead of piling up: the newest in front, up to two behind it showing only their
 * edge. Hovering or focusing the stack (or tapping it, on a touch screen) opens it into a list and
 * pauses every timer, so nothing disappears while it is being read; on a phone a toast is dismissed
 * by dragging it right or down. Errors still never leave on their own. Under reduced motion the
 * stack is always open and nothing moves.
 */
export function ToastProvider({ children, limit = 4 }: ToastProviderProps) {
  const [toasts, setToasts] = useState<readonly ToastRecord[]>([]);
  const [leaving, setLeaving] = useState<ReadonlySet<string>>(() => new Set());
  const [heights, setHeights] = useState<Readonly<Record<string, number>>>({});
  const [hovered, setHovered] = useState(false);
  const [tabHidden, setTabHidden] = useState(false);
  const [reduced] = useState(prefersReducedMotion);
  const timersRef = useRef(new Map<string, Timer>());
  const regionRef = useRef<HTMLDivElement | null>(null);
  const paused = hovered || tabHidden;
  const pausedRef = useRef(paused);

  const remove = useCallback((id: string) => {
    const timer = timersRef.current.get(id);
    if (timer?.handle !== undefined) {
      clearTimeout(timer.handle);
    }
    timersRef.current.delete(id);
    setToasts((current) => current.filter((toast) => toast.id !== id));
    setLeaving((current) => {
      const next = new Set(current);
      next.delete(id);
      return next;
    });
    setHeights((current) => {
      const { [id]: _gone, ...rest } = current;
      return rest;
    });
  }, []);

  /** Plays the exit, then removes. `immediate`: the toast already animated itself away (a swipe). */
  const dismiss = useCallback(
    (id: string, immediate = false) => {
      const timer = timersRef.current.get(id);
      if (timer?.handle !== undefined) {
        clearTimeout(timer.handle);
        timer.handle = undefined;
      }
      if (immediate || reduced) {
        remove(id);
        return;
      }
      setLeaving((current) => new Set(current).add(id));
      setTimeout(() => {
        remove(id);
      }, EXIT_MS);
    },
    [reduced, remove],
  );

  const arm = useCallback(
    (id: string, timer: Timer) => {
      timer.startedAt = Date.now();
      timer.handle = setTimeout(() => {
        dismiss(id);
      }, timer.remaining);
    },
    [dismiss],
  );

  const show = useCallback(
    (options: ToastOptions) => {
      sequence += 1;
      const id = `toast-${String(sequence)}`;
      const record: ToastRecord = {
        ...options,
        id,
        tone: options.tone ?? 'neutral',
        durationMs: options.durationMs ?? DEFAULT_DURATION_MS,
      };

      setToasts((current) => [...current, record].slice(-limit));

      if (record.durationMs > 0) {
        const timer: Timer = { handle: undefined, remaining: record.durationMs, startedAt: 0 };
        timersRef.current.set(id, timer);
        if (!pausedRef.current) {
          arm(id, timer);
        }
      }

      return id;
    },
    [arm, limit],
  );

  // Every timer stops while the stack is open (being read) or the tab is hidden, and picks up where
  // it left off afterwards.
  useEffect(() => {
    pausedRef.current = paused;
    for (const [id, timer] of timersRef.current) {
      if (paused && timer.handle !== undefined) {
        clearTimeout(timer.handle);
        timer.handle = undefined;
        timer.remaining = Math.max(500, timer.remaining - (Date.now() - timer.startedAt));
      } else if (!paused && timer.handle === undefined) {
        arm(id, timer);
      }
    }
  }, [paused, arm]);

  useEffect(() => {
    const onVisibility = () => {
      setTabHidden(document.visibilityState === 'hidden');
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  // A tap elsewhere closes a stack opened by touch.
  useEffect(() => {
    if (!hovered) {
      return;
    }
    const onPointerDown = (event: PointerEvent) => {
      if (regionRef.current !== null && !regionRef.current.contains(event.target as Node)) {
        setHovered(false);
      }
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
    };
  }, [hovered]);

  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      for (const timer of timers.values()) {
        if (timer.handle !== undefined) {
          clearTimeout(timer.handle);
        }
      }
      timers.clear();
    };
  }, []);

  const api = useMemo<ToastApi>(
    () => ({
      show,
      success: (title, description) =>
        show(
          description === undefined
            ? { title, tone: 'success' }
            : { title, description, tone: 'success' },
        ),
      error: (title, description) =>
        show(
          description === undefined
            ? { title, tone: 'danger', durationMs: 0 }
            : { title, description, tone: 'danger', durationMs: 0 },
        ),
      dismiss: (id: string) => {
        dismiss(id);
      },
      dismissAll: () => {
        for (const timer of timersRef.current.values()) {
          if (timer.handle !== undefined) {
            clearTimeout(timer.handle);
          }
        }
        timersRef.current.clear();
        setToasts([]);
        setLeaving(new Set());
      },
    }),
    [show, dismiss],
  );

  const onHeight = useCallback((id: string, height: number) => {
    setHeights((current) => (current[id] === height ? current : { ...current, [id]: height }));
  }, []);

  // Positions, newest first: collapsed, each one behind sits a little higher and smaller, showing
  // only its edge; open, they lie one above the other at their own heights.
  const open = hovered || reduced;
  const newestFirst = [...toasts].reverse();
  const frontHeight = heights[newestFirst[0]?.id ?? ''] ?? 0;
  let offset = 0;
  const layout = newestFirst.map((toast, index) => {
    const height = heights[toast.id] ?? 0;
    const position = open
      ? { y: -offset, scale: 1, hidden: false, back: false, height }
      : {
          y: -Math.min(index, VISIBLE - 1) * PEEK_PX,
          scale: 1 - 0.06 * Math.min(index, VISIBLE - 1),
          hidden: index >= VISIBLE,
          back: index > 0,
          height: index === 0 ? height : frontHeight,
        };
    offset += height + GAP_PX;
    return { toast, index, ...position };
  });
  const regionHeight =
    toasts.length === 0
      ? 0
      : open
        ? offset - GAP_PX
        : frontHeight + (Math.min(toasts.length, VISIBLE) - 1) * PEEK_PX;

  return (
    <ToastContext.Provider value={api}>
      {children}
      {/* `polite` rather than `assertive`: a toast never interrupts what the user is doing. */}
      <div
        ref={regionRef}
        className={styles.region}
        role="status"
        aria-live="polite"
        aria-label="Avisos"
        data-open={open ? '' : undefined}
        style={{ blockSize: `${String(regionHeight)}px` }}
        onPointerEnter={(event) => {
          if (event.pointerType === 'mouse') {
            setHovered(true);
          }
        }}
        onPointerLeave={(event) => {
          if (
            event.pointerType === 'mouse' &&
            !regionRef.current?.contains(document.activeElement)
          ) {
            setHovered(false);
          }
        }}
        onFocus={() => {
          setHovered(true);
        }}
        onBlur={(event) => {
          const region = regionRef.current;
          if (
            region !== null &&
            !region.contains(event.relatedTarget as Node | null) &&
            !region.matches(':hover')
          ) {
            setHovered(false);
          }
        }}
      >
        {layout.map(({ toast, index, y, scale, hidden, back, height }) => (
          <Toast
            key={toast.id}
            toast={toast}
            onDismiss={dismiss}
            onHeight={onHeight}
            onTap={() => {
              setHovered((value) => !value);
            }}
            draggable={open || index === 0}
            leaving={leaving.has(toast.id)}
            back={back}
            style={
              {
                '--y': `${String(y)}px`,
                '--scale': String(scale),
                zIndex: 10 - index,
                blockSize: height > 0 ? `${String(height)}px` : undefined,
                opacity: hidden ? 0 : undefined,
                pointerEvents: hidden ? 'none' : undefined,
              } as CSSProperties
            }
          />
        ))}
      </div>
    </ToastContext.Provider>
  );
}
